import type { Observation } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import { Stack, Switch, Text, cn } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { useTranslation } from '@eluve/i18n';
import { useMedplum, useMedplumPatientAccess } from '@eluve/medplum-client';

import { ExtractedObservations } from './ExtractedObservations';
import { ReviewDocumentModal } from './ReviewDocumentModal';
import { useTabActionsSlot } from './TabActionsSlotContext';

// Meta-tag marking a resource as agent-produced (matches the backend).
const AGENT_TAG_SYSTEM = 'https://eluve.com/agent';

type ObservationView = 'by-report' | 'trends';

/** One report = one column in the trend pivot (a point in time). */
interface PivotColumn {
  reportId: string;
  title: string;
  /** ISO date used to sort + label the column; null if the report has none. */
  date: string | null;
  /** Analyte name (Observation.code.text) → its Observation in this report. */
  byAnalyte: Map<string, Observation>;
}

/** "13.5 g/dL", "Positive", or "—" when a report has no value for an analyte. */
function formatValue(obs: Observation | undefined): string {
  if (!obs) return '—';
  if (obs.valueQuantity) {
    const { value, unit } = obs.valueQuantity;
    return unit ? `${value} ${unit}` : `${value}`;
  }
  return obs.valueString ?? '—';
}

/** Best-effort out-of-range flag from the free-text reference range
 *  ("70-99", "<200", ">40"); null when unparseable. */
function outOfRange(obs: Observation | undefined): 'high' | 'low' | null {
  const value = obs?.valueQuantity?.value;
  if (value == null) return null;
  const text = obs?.referenceRange?.[0]?.text;
  if (!text) return null;

  const span = text.match(/(-?\d+\.?\d*)\s*(?:[-–]|to)\s*(-?\d+\.?\d*)/i);
  if (span) {
    const low = Number(span[1]);
    const high = Number(span[2]);
    if (value < low) return 'low';
    if (value > high) return 'high';
    return null;
  }
  const upper = text.match(/[<≤]\s*(-?\d+\.?\d*)/);
  if (upper) return value > Number(upper[1]) ? 'high' : null;
  const lower = text.match(/[>≥]\s*(-?\d+\.?\d*)/);
  if (lower) return value < Number(lower[1]) ? 'low' : null;
  return null;
}

/**
 * Patient "Observations" tab (under Medical Profile): a **By report** view
 * (clusters Observations under their DiagnosticReport with the review/approve
 * flow, via {@link ExtractedObservations}) and a **Trends** pivot (analyte ×
 * report-date) for scanning values over time.
 */
export const Observations: React.FC = () => {
  const { t } = useTranslation();
  const { patientId } = useParams() as { patientId: string };
  const { client } = useMedplum();
  const { fhirPatientId, isLoading: isResolvingPatient } =
    useMedplumPatientAccess(patientId);
  // Tab bar slot to portal the by-report/trends toggle into, so it renders
  // beside the tabs as this tab's actions.
  const actionsSlot = useTabActionsSlot();

  const [view, setView] = useState<ObservationView>('by-report');
  const [columns, setColumns] = useState<PivotColumn[]>([]);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);
  // Source-document id being reviewed in the side-by-side modal, or null.
  const [reviewDocId, setReviewDocId] = useState<string | null>(null);
  // Bumped after a review closes to remount the list so approved reports drop
  // out of "Needs review".
  const [byReportKey, setByReportKey] = useState(0);

  const loadTrends = useCallback(async () => {
    if (!client || !fhirPatientId) return;
    setIsLoadingTrends(true);
    try {
      const reports = await client.searchResources('DiagnosticReport', {
        subject: `Patient/${fhirPatientId}`,
        _count: 100,
        _sort: '-_lastUpdated',
      });
      const agentReports = reports.filter(
        (r) =>
          r.status !== 'entered-in-error' &&
          r.meta?.tag?.some((t) => t.system === AGENT_TAG_SYSTEM),
      );

      const pivot = await Promise.all(
        agentReports.map(async (report): Promise<PivotColumn> => {
          const resultIds = (report.result ?? [])
            .map((ref) => ref.reference?.replace('Observation/', ''))
            .filter((id): id is string => Boolean(id));

          let observations: Observation[] = [];
          if (resultIds.length > 0) {
            try {
              const fetched = await client.searchResources('Observation', {
                _id: resultIds.join(','),
                _count: resultIds.length,
              });
              // `_id=a,b,c` doesn't preserve the listed order — restore the
              // report's `result[]` (source-document) order so analyte rows
              // read in the same order as the report.
              const byId = new Map(fetched.map((o) => [o.id, o]));
              observations = resultIds.flatMap((id) => {
                const o = byId.get(id);
                return o ? [o] : [];
              });
            } catch {
              observations = [];
            }
          }

          const byAnalyte = new Map<string, Observation>();
          for (const obs of observations) {
            if (obs.status === 'entered-in-error') continue;
            const name = obs.code?.text?.trim();
            if (name) byAnalyte.set(name, obs);
          }

          return {
            reportId: report.id!,
            title:
              report.code?.text ?? t('patients.observations.reportFallback'),
            date:
              report.effectiveDateTime ??
              report.issued ??
              report.meta?.lastUpdated ??
              null,
            byAnalyte,
          };
        }),
      );

      // Oldest → newest, so trends read left to right.
      pivot.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
      setColumns(pivot);
    } catch {
      setColumns([]);
    } finally {
      setIsLoadingTrends(false);
    }
  }, [client, fhirPatientId, t]);

  useEffect(() => {
    if (view === 'trends') void loadTrends();
  }, [view, loadTrends]);

  // Analyte rows: every distinct Observation name across reports, in first-seen
  // (oldest-report-first) order so a panel's analytes stay roughly together.
  const analytes = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const col of columns) {
      for (const name of col.byAnalyte.keys()) {
        if (!seen.has(name)) {
          seen.add(name);
          ordered.push(name);
        }
      }
    }
    return ordered;
  }, [columns]);

  if (isResolvingPatient) {
    return (
      <Text className="text-text-secondary">
        {t('patients.common.loading')}
      </Text>
    );
  }
  if (!client || !fhirPatientId) {
    return (
      <Text className="text-text-secondary">
        {t('patients.common.noFhirRecord')}
      </Text>
    );
  }

  const viewToggle = (
    <div className="flex items-center gap-2">
      <Text
        size="sm"
        color={view === 'trends' ? 'primary' : 'tertiary'}
        weight={view === 'trends' ? 'medium' : 'regular'}
      >
        {t('patients.observations.showTrends')}
      </Text>
      <Switch
        checked={view === 'trends'}
        onCheckedChange={(checked) => setView(checked ? 'trends' : 'by-report')}
        aria-label={t('patients.observations.showTrends')}
      />
    </div>
  );

  return (
    <Stack gap={4}>
      {actionsSlot && createPortal(viewToggle, actionsSlot)}

      {view === 'by-report' ? (
        <ExtractedObservations
          key={byReportKey}
          client={client}
          fhirPatientId={fhirPatientId}
          onReview={setReviewDocId}
        />
      ) : isLoadingTrends ? (
        <Text className="text-text-secondary">
          {t('patients.observations.loadingTrends')}
        </Text>
      ) : analytes.length === 0 ? (
        <Text className="text-text-secondary">
          {t('patients.observations.noneToChart')}
        </Text>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border-primary">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="bg-secondary">
                <th className="sticky left-0 z-10 bg-secondary px-3 py-2 text-left font-medium text-text-secondary">
                  {t('patients.observations.analyte')}
                </th>
                {columns.map((col) => (
                  <th
                    key={col.reportId}
                    className="px-3 py-2 text-right font-medium whitespace-nowrap text-text-secondary"
                    title={col.title}
                  >
                    {col.date
                      ? formatDate.toDisplay(col.date)
                      : t('patients.observations.undated')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {analytes.map((name) => (
                <tr key={name} className="border-t border-border-secondary">
                  <td className="sticky left-0 z-10 bg-background px-3 py-2 font-medium whitespace-nowrap text-text-primary">
                    {name}
                  </td>
                  {columns.map((col) => {
                    const obs = col.byAnalyte.get(name);
                    const flag = outOfRange(obs);
                    return (
                      <td
                        key={col.reportId}
                        className={cn(
                          'px-3 py-2 text-right whitespace-nowrap tabular-nums',
                          obs ? 'text-text-primary' : 'text-text-tertiary',
                          flag && 'font-semibold text-text-negative',
                        )}
                      >
                        {formatValue(obs)}
                        {flag === 'high' && ' ↑'}
                        {flag === 'low' && ' ↓'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ReviewDocumentModal
        client={client}
        fhirPatientId={fhirPatientId}
        documentReferenceId={reviewDocId}
        onOpenChange={(open) => {
          if (!open) {
            setReviewDocId(null);
            // Remount the list so a just-approved report leaves "Needs review".
            setByReportKey((k) => k + 1);
          }
        }}
      />
    </Stack>
  );
};
